
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { Region } from "@/store/types";
import { getLocalRegion } from "@/utils/region-utils";

import vselect3 from "vselect3";

const Regions = namespace("regions");

/**
 * Re-usable component which provides the user with a select containing regions they have access too.
 */
@Component({
	components: {
		"v-select": vselect3
	}
})
export default class RegionSelect extends Vue {
	@Regions.State("regions") public regionMap: { [regionId: number]: Region };

	@Regions.Action loadRegions: () => Promise<void>;
	@Regions.Action loginToRegion: ({
		regionId,
		overwriteLocalLogin
	}: {
		regionId: number;
		overwriteLocalLogin: boolean;
	}) => Promise<void>;
	@Regions.Action logoutRegion: (regionId: number) => Promise<void>;

	/**
	 * The currently selected region.
	 */
	@Prop({ default: () => getLocalRegion() }) public value: Region;

	/**
	 * If set to true, when the user selects a region we'll attempt to login to it and store the result. The select
	 * will also handle logging the user out of any previously selected region when they change their selection.
	 * Useful when you need to provide cross-region functionality in the UI without having to hit the DS.
	 */
	@Prop({ default: true }) public manageRegionLogins: boolean;

	private connectingToRegion: boolean = false;
	private currentRegionId: number = 0;

	/**
	 * Returns an array of regions the current user has access too, including an option for the local one.
	 */
	private get regions(): Region[] {
		var regionList = Object.values(this.regionMap);
		regionList.unshift(getLocalRegion());

		return regionList;
	}

	/**
	 * Returns true if the user has access to any regions (other than the local one).
	 */
	private get hasRegionsAvailable() {
		return this.regions.length > 1;
	}

	/**
	 * Mounted lifecycle hook.
	 */
	private async mounted() {
		// Emit a component-loaded event, in case we're in a generic modal.
		this.$emit("component-loaded");
	}

	private async beforeDestroy() {
		if (this.value.regionId > 0) {
			await this.logoutRegion(this.value.regionId);
		}
	}

	private async updateRegionLogins(newRegion: Region, previousRegion: Region) {
		if (!this.manageRegionLogins || this.currentRegionId == newRegion.regionId) {
			return;
		}

		if (previousRegion.regionId > 0) {
			// If we're managing logins and currently logged in to a region, logout.
			await this.logoutRegion(previousRegion.regionId);
		}

		if (newRegion.regionId > 0) {
			// If we're managing logins and our new region isn't the local one, login to it, and emit our input
			// event when we're finished.
			try {
				this.connectingToRegion = true;
				await this.loginToRegion({
					regionId: newRegion.regionId,
					overwriteLocalLogin: false
				});
				this.connectingToRegion = false;
			} catch (err) {
				this.$notify({
					type: "error",
					title: "Error connecting to region",
					text: `The region ${newRegion.title} isn't currently available, please try again later or contact support.`
				});

				this.connectingToRegion = false;

				// Return without emitting the new region change. We couldn't login, so we can't select it.
				return;
			}
		}

		this.currentRegionId = newRegion.regionId;
	}

	/**
	 * Handler for when the user selects a new region. If the select has been setup to auto-login,
	 * we'll attempt to login to the region.
	 */
	private async onRegionSelected(region: Region) {
		await this.updateRegionLogins(region, this.value);
		this.$emit("input", region);
	}

	/**
	 * Handler for when the user opens the region select. Refreshes the list of regions.
	 */
	private async onSelectOpened() {
		// Update the list of regions in case one of them has gone down.
		await this.loadRegions();
	}

	/**
	 * Watcher for value changes by the parent component. Ensures we're logged in/out correctly if the value
	 * is changed outside of the region select.
	 */
	@Watch("value")
	private async onValueChanged(newValue: Region, oldValue: Region) {
		await this.updateRegionLogins(newValue, oldValue);
	}
}
